import {generateKeyBetween, generateNKeysBetween} from 'fractional-indexing';

import {
  type PanelPlacementOverride,
  SectionPanelSorting,
} from '../../../components/PanelBank/types';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../../util/panelbankTypes';
import * as Panels from '../../../util/panels';
import {isSparseOverridesEnabled} from '../../../util/sparseOverridesFeatureFlag';
import {setInShallowClone} from '../../../util/utility';
import type * as PanelTypes from '../panel/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {type ActionType, type ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import {
  type ActiveData,
  getMovePanelData,
  getUndoData,
  handleMovePanelToDiffSection,
  handleMovePanelToSameSection,
  type TargetData,
} from './movePanelUtils';
import type * as Types from './types';
import {getPanelBankConfig} from './utils';

/**
 * Checks if a panel is eligible for sparse placement overrides.
 *
 * A panel is eligible if:
 * - It has a stable key (from getOverrideKey - either metric-based or custom:__id__)
 * - It's not in the Hidden Panels section
 * - No other panel in the panel bank has the same key (collision check)
 */
function isPanelEligibleForSparseOverride(
  state: ViewReducerState,
  panelRef: PanelTypes.Ref,
  sectionRef: PanelBankSectionConfigTypes.Ref,
  panelBankConfigRef: Types.Ref
): {isEligible: boolean; panelKey: string | null} {
  const panel = state.parts[panelRef.type][panelRef.id];
  // Use getOverrideKey to support both metric-based and custom:__id__ keys
  const panelKey = Panels.getOverrideKey(panel);

  // Panel must have a stable key
  if (panelKey == null) {
    return {isEligible: false, panelKey: null};
  }

  // Hidden Panels is a bookkeeping section that tracks deleted/hidden panels
  // as tombstones to prevent auto-gen from re-adding them. These panels
  // shouldn't get placement overrides since they're not visible.
  const section = state.parts[sectionRef.type][sectionRef.id];
  if (section.name === PANEL_BANK_HIDDEN_SECTION_NAME) {
    return {isEligible: false, panelKey};
  }

  // Check for key collisions across all sections
  const panelBankConfig = getPanelBankConfig(state, panelBankConfigRef);
  let keyCount = 0;

  for (const secRef of panelBankConfig.sectionRefs) {
    const sec = state.parts[secRef.type][secRef.id];
    for (const pRef of sec.panelRefs) {
      const p = state.parts[pRef.type][pRef.id];
      // Use getOverrideKey for collision check too
      if (Panels.getOverrideKey(p) === panelKey) {
        keyCount++;
        if (keyCount > 1) {
          // Collision detected
          return {isEligible: false, panelKey};
        }
      }
    }
  }

  return {isEligible: true, panelKey};
}

/**
 * Computes an order key that sorts before ALL existing panels in the section.
 * Used when moving a panel to the head of a section via the dropdown menu.
 *
 * The smallest effective key is either positionalKeys[0] (the first
 * non-overridden panel) or the smallest non-orphan override for this section,
 * whichever sorts first.
 */
function computeOrderKeyForHeadOfSection(
  targetSection: PanelBankSectionConfigTypes.PanelBankSectionConfigNormalized,
  overrides: Record<string, PanelPlacementOverride> | undefined
): string {
  const sectionId = targetSection.__id__;

  // First positional key (smallest possible for non-overridden panels)
  const firstPositionalKey = generateNKeysBetween(null, null, 1)[0];

  // Find the smallest non-orphan override orderKey for this section
  let smallestOverrideKey: string | null = null;
  if (overrides) {
    for (const override of Object.values(overrides)) {
      if (
        override.sectionId === sectionId &&
        (smallestOverrideKey == null || override.orderKey < smallestOverrideKey)
      ) {
        smallestOverrideKey = override.orderKey;
      }
    }
  }

  const smallestKey =
    smallestOverrideKey != null && smallestOverrideKey < firstPositionalKey
      ? smallestOverrideKey
      : firstPositionalKey;

  return generateKeyBetween(null, smallestKey);
}

/**
 * Computes the order key for a panel being inserted at a given position.
 *
 * Neighbors are determined by the effective sorted order, not array position.
 *
 * When insertDir="before":
 * - beforeKey = key of the panel before the target in sorted order (if any)
 * - afterKey = target's key
 * - Result: a key that sorts before the target
 *
 * When insertDir="after":
 * - beforeKey = target's key
 * - afterKey = key of the panel after the target in sorted order (if any)
 * - Result: a key that sorts after the target
 */
function computeOrderKeyForInsert(
  state: ViewReducerState,
  targetSection: PanelBankSectionConfigTypes.PanelBankSectionConfigNormalized,
  targetPanelIndex: number,
  insertDir: 'before' | 'after',
  activePanelRef: PanelTypes.Ref,
  overrides: Record<string, PanelPlacementOverride> | undefined
): string {
  const panelRefs = targetSection.panelRefs;
  const sectionId = targetSection.__id__;

  // Find the target panel (the anchor for the move)
  const targetPanelRef = panelRefs[targetPanelIndex];

  // Count only panels that DON'T have overrides for positional key generation.
  // This keeps the positional key space stable when panels with overrides
  // (custom panels, duplicates) are added or removed — matching the logic
  // in applySectionOrdering (hooks.ts).
  type PanelWithKey = {
    ref: PanelTypes.Ref;
    arrayIndex: number;
    effectiveKey: string;
    panelKey: string | null;
  };

  // First pass: identify which panels have overrides (excluding the active panel)
  const panelOverrideStatus: Array<{
    ref: PanelTypes.Ref;
    panel: any;
    panelKey: string | null;
    hasOverride: boolean;
    isActive: boolean;
  }> = [];
  let nonOverriddenCount = 0;
  for (let i = 0; i < panelRefs.length; i++) {
    const ref = panelRefs[i];
    const isActive = ref.id === activePanelRef.id;
    const panel = state.parts[ref.type][ref.id];
    const panelKey = Panels.getOverrideKey(panel);
    const hasOverride =
      panelKey != null &&
      overrides != null &&
      panelKey in overrides &&
      overrides[panelKey].sectionId === sectionId;
    if (!hasOverride && !isActive) {
      nonOverriddenCount++;
    }
    panelOverrideStatus.push({ref, panel, panelKey, hasOverride, isActive});
  }

  const positionalKeys = generateNKeysBetween(
    null,
    null,
    nonOverriddenCount || 1
  );

  // Second pass: build effective keys
  let positionalIndex = 0;
  const panelsWithKeys: PanelWithKey[] = [];
  const currentEffectiveKeysByPanelId: Record<string, string> = {};
  const currentPositionalKeys = generateNKeysBetween(
    null,
    null,
    panelOverrideStatus.filter(p => !p.hasOverride).length || 1
  );
  let currentPositionalIndex = 0;

  for (const {ref, panelKey, hasOverride, isActive} of panelOverrideStatus) {
    currentEffectiveKeysByPanelId[ref.id] = hasOverride
      ? overrides![panelKey!].orderKey
      : currentPositionalKeys[currentPositionalIndex++];

    if (isActive) {
      continue;
    }
    let effectiveKey: string;
    if (hasOverride) {
      effectiveKey = overrides![panelKey!].orderKey;
    } else {
      effectiveKey = positionalKeys[positionalIndex++];
    }
    panelsWithKeys.push({
      ref,
      arrayIndex: panelOverrideStatus.findIndex(p => p.ref === ref),
      effectiveKey,
      panelKey,
    });
  }

  // Sort by effective key using simple lexicographic comparison (NOT localeCompare)
  // This must match the ordering used by the fractional-indexing library
  panelsWithKeys.sort((a, b) => {
    if (a.effectiveKey < b.effectiveKey) {
      return -1;
    }
    if (a.effectiveKey > b.effectiveKey) {
      return 1;
    }
    return 0;
  });

  // Find the target panel in the sorted list
  const targetSortedIndex = panelsWithKeys.findIndex(
    p => p.ref.id === targetPanelRef.id
  );

  if (targetSortedIndex === -1) {
    // Target panel not found (shouldn't happen, but handle gracefully)
    // Just insert at the beginning
    return generateKeyBetween(null, panelsWithKeys[0]?.effectiveKey ?? null);
  }

  // For same-section moves, dnd-kit always passes insertDir='before'.
  // Since panelRefs stays in canonical order (not display order), we detect
  // forward/backward using effective keys — which reflect the actual display
  // order that the user sees and drags within.
  //   Forward (active sorts before target): panel moves past target → 'after'
  //   Backward (active sorts after target): panel moves to target's slot → 'before'
  // Cross-section moves compute insertDir properly from the drop indicator.
  let effectiveInsertDir = insertDir;

  if (insertDir === 'before') {
    const activePanelEffectiveKey =
      currentEffectiveKeysByPanelId[activePanelRef.id];
    const targetEffectiveKey = currentEffectiveKeysByPanelId[targetPanelRef.id];

    if (activePanelEffectiveKey != null && targetEffectiveKey != null) {
      if (activePanelEffectiveKey < targetEffectiveKey) {
        effectiveInsertDir = 'after';
      }
    }
  }

  // Determine neighbors based on effectiveInsertDir
  let beforeKey: string | null = null;
  let afterKey: string | null = null;

  if (effectiveInsertDir === 'before') {
    // Insert BEFORE target in sorted order
    // beforeKey = panel before target in sorted order (if exists)
    // afterKey = target's key
    if (targetSortedIndex > 0) {
      beforeKey = panelsWithKeys[targetSortedIndex - 1].effectiveKey;
    }
    afterKey = panelsWithKeys[targetSortedIndex].effectiveKey;
  } else {
    // effectiveInsertDir === 'after'
    // Insert AFTER target in sorted order
    // beforeKey = target's key
    // afterKey = panel after target in sorted order (if exists)
    beforeKey = panelsWithKeys[targetSortedIndex].effectiveKey;
    if (targetSortedIndex + 1 < panelsWithKeys.length) {
      afterKey = panelsWithKeys[targetSortedIndex + 1].effectiveKey;
    }
  }

  // Validate that beforeKey < afterKey (if both are non-null)
  if (beforeKey != null && afterKey != null && beforeKey >= afterKey) {
    // This should never happen if sorting is correct
    // Log detailed info for debugging
    console.error('[computeOrderKeyForInsert] INVALID ORDER DETECTED!', {
      beforeKey,
      afterKey,
      beforeKeyCharCode: beforeKey.charCodeAt(0),
      afterKeyCharCode: afterKey.charCodeAt(0),
      effectiveInsertDir,
      targetSortedIndex,
      sortedPanelsSample: panelsWithKeys.slice(0, 10).map((p, idx) => ({
        sortedIndex: idx,
        key: p.panelKey,
        effectiveKey: p.effectiveKey,
        effectiveKeyCharCode: p.effectiveKey.charCodeAt(0),
      })),
    });
    // Try to recover by inserting after the "larger" key
    return generateKeyBetween(beforeKey, null);
  }

  // Generate a key between the neighbors
  const orderKey = generateKeyBetween(beforeKey, afterKey);

  return orderKey;
}

/**
 * This actions handles moving the active panel to the target section's position in the array.
 * This reorders the panelRefs in the panelBankSectionConfig. This assumes you're moving the
 * panel WITHIN THE SAME SECTION. Please use movePanelToDiffSection for moving a panel to a different section.
 *
 * @param ref This is the panel bank config ref
 * @param active This is the panel that is being moved
 * @param target This is the info that indicates what position the active panel will be moved to and which section
 */
export const movePanelAction = (
  state: ViewReducerState,
  ref: Types.Ref,
  active: {
    panelRef: PanelTypes.Ref;
    sectionRef: PanelBankSectionConfigTypes.Ref;
  },
  target: {
    panelRef: PanelTypes.Ref | null; // a section can be empty, so the panelRef can be null
    sectionRef: PanelBankSectionConfigTypes.Ref;
    insertDir: 'before' | 'after'; // This is the direction the panel will be inserted in the target section
  }
): [ViewReducerState, ActionType] => {
  const movePanelData = getMovePanelData(state, active, target);
  const {
    activePanelIndex,
    targetPanelIndex,
    isMovingToSameSection,
    isMovingToSamePosition,
    activeSection,
    targetSection,
  } = movePanelData;

  // Validate that active panel exists
  // Note: targetPanelIndex can be -1 if target section is empty (target.panelRef is null)
  const isTargetSectionEmpty = target.panelRef === null;
  if (activePanelIndex === -1) {
    console.warn('movePanelAction: Active panel not found', {
      activePanelRefId: active.panelRef.id,
      activeIndex: activePanelIndex,
      activeSectionName: activeSection.name,
    });
    return [state, Actions.noOp(ref)];
  }

  // Validate target panel exists (unless section is empty)
  if (targetPanelIndex === -1 && !isTargetSectionEmpty) {
    console.warn('movePanelAction: Target panel not found', {
      targetPanelRefId: target.panelRef?.id,
      targetIndex: targetPanelIndex,
      targetSectionName: targetSection.name,
      targetSectionPanelCount: targetSection.panelRefs.length,
      isTargetPanelInTargetSection: target.panelRef
        ? targetSection.panelRefs.some(r => r.id === target.panelRef?.id)
        : 'n/a (target is null)',
    });
    return [state, Actions.noOp(ref)];
  }

  // If moving to the same section and position, no-op
  if (isMovingToSameSection && isMovingToSamePosition) {
    return [state, Actions.noOp(ref)];
  }

  // Check if panel is eligible for sparse overrides
  const {isEligible, panelKey} = isPanelEligibleForSparseOverride(
    state,
    active.panelRef,
    active.sectionRef,
    ref
  );

  let newState: ViewReducerState;

  // Grab these values before any state updates so we can use them in undo action
  const prevPanelIsAuto =
    state.parts[active.panelRef.type][active.panelRef.id].isAuto;
  const prevActiveSectionIsPanelsAuto = activeSection.isPanelsAuto;
  const prevTargetSectionIsPanelsAuto = targetSection.isPanelsAuto;

  // Get current overrides for undo
  const panelBankConfig = getPanelBankConfig(state, ref);
  const existingOverrides = panelBankConfig.panelPlacementOverrides;
  const existingOverride =
    panelKey != null && existingOverrides
      ? existingOverrides[panelKey]
      : undefined;

  const activeData = {
    panelIndex: activePanelIndex,
    panelRef: active.panelRef,
    sectionRef: active.sectionRef,
    section: activeSection,
  };

  const targetData = {
    panelIndex: targetPanelIndex,
    insertDir: target.insertDir,
    panelRef: target.panelRef,
    sectionRef: target.sectionRef,
    section: targetSection,
  };

  const sparseOverridesEnabled = isSparseOverridesEnabled();

  // When sparse overrides are enabled and the panel is eligible, skip the
  // physical arrayMove. The override IS the move — the rendering hook
  // (applySectionOrdering) handles display order. Keeping panelRefs in
  // canonical order ensures positional keys are always stable.
  const usesSparseOverride =
    sparseOverridesEnabled && isEligible && panelKey != null;

  if (usesSparseOverride && isMovingToSameSection) {
    // Same-section: no physical rearrangement needed
    newState = state;
  } else if (!isMovingToSameSection) {
    newState = handleMovePanelToDiffSection(state, activeData, targetData);
  } else {
    newState = handleMovePanelToSameSection(state, activeData, targetData);
  }

  // When sparse overrides are enabled and the panel is eligible, record a
  // placement override for this move. The fallback path below (for ineligible
  // panels) still sets isPanelsAuto=false on the section.
  if (usesSparseOverride) {
    // Compute the order key for the new position
    let orderKey: string;

    if (isTargetSectionEmpty && targetSection.panelRefs.length === 0) {
      // Truly empty section - generate initial key
      orderKey = generateKeyBetween(null, null);
    } else if (isTargetSectionEmpty) {
      // Menu move (panelRef: null) into a non-empty section — place at the
      // absolute front by generating a key before the smallest effective key.
      orderKey = computeOrderKeyForHeadOfSection(
        targetSection,
        existingOverrides
      );
    } else {
      orderKey = computeOrderKeyForInsert(
        state,
        targetSection,
        targetPanelIndex,
        target.insertDir,
        active.panelRef,
        existingOverrides
      );
    }

    // Get the target section's stable __id__
    const targetSectionId = targetSection.__id__;

    // Create the new override
    const newOverride: PanelPlacementOverride = {
      sectionId: targetSectionId,
      orderKey,
    };

    // Update the overrides map
    const newOverrides: Record<string, PanelPlacementOverride> = {
      ...existingOverrides,
      [panelKey]: newOverride,
    };

    newState = setInShallowClone(
      newState,
      ['parts', ref.type, ref.id, 'panelPlacementOverrides'],
      newOverrides
    );

    // If the section was alphabetically sorted, disable it — the user is
    // explicitly choosing a custom position by dragging. applySectionOrdering
    // skips overrides for alphabetically sorted sections, so we need to
    // clear the sort for the override to take effect.
    if (targetSection.sorted === SectionPanelSorting.Alphabetical) {
      newState = setInShallowClone(
        newState,
        ['parts', target.sectionRef.type, target.sectionRef.id, 'sorted'],
        SectionPanelSorting.None
      );
    }

    // Create undo action with override info
    const undoAction = createUndoMoveActionDataWithOverride(
      ref,
      newState,
      {
        ...activeData,
        prevIsPanelAuto: prevPanelIsAuto,
        prevSectionPanelsAuto: prevActiveSectionIsPanelsAuto,
      },
      {
        ...targetData,
        prevSectionPanelsAuto: prevTargetSectionIsPanelsAuto,
      },
      panelKey,
      existingOverride
    );

    recordRumAction('sparse_overrides.move', {
      usedSparseOverride: true,
      isCrossSectionMove: !isMovingToSameSection,
      orderKeyLength: orderKey.length,
    });

    // Update defaultMoveToSectionName for cross-section moves
    if (!isMovingToSameSection) {
      const toSectionName =
        state.parts[target.sectionRef.type][target.sectionRef.id].name;
      const prevSettings = state.parts[ref.type][ref.id].settings;
      newState = setInShallowClone(
        newState,
        ['parts', ref.type, ref.id, 'settings'],
        {
          ...prevSettings,
          defaultMoveToSectionName: toSectionName,
        }
      );
    }

    return [newState, undoAction];
  }

  // Fall back to existing behavior for ineligible panels
  const undoAction = createUndoMoveActionData(
    ref,
    newState,
    {
      ...activeData,
      prevIsPanelAuto: prevPanelIsAuto,
      prevSectionPanelsAuto: prevActiveSectionIsPanelsAuto,
    },
    {
      ...targetData,
      prevSectionPanelsAuto: prevTargetSectionIsPanelsAuto,
    }
  );

  // Update the active panel's information
  newState = setInShallowClone(
    newState,
    ['parts', active.panelRef.type, active.panelRef.id, 'isAuto'],
    false
  );

  newState = setInShallowClone(
    newState,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'isPanelsAuto'],
    false
  );

  if (!isMovingToSameSection) {
    newState = setInShallowClone(
      newState,
      ['parts', target.sectionRef.type, target.sectionRef.id, 'isPanelsAuto'],
      false
    );

    // Save the toSection as the new defaultMoveToSectionName
    const toSectionName =
      state.parts[target.sectionRef.type][target.sectionRef.id].name;
    const prevSettings = state.parts[ref.type][ref.id].settings;
    newState = setInShallowClone(
      newState,
      ['parts', ref.type, ref.id, 'settings'],
      {
        ...prevSettings,
        defaultMoveToSectionName: toSectionName,
      }
    );
  }

  // Update the section's information
  // set manual sorting to section chart was moved to
  // TODO: improve this by checking if the panel was placed
  // in a position respecting or automatically placing it in
  // the sorted position and showing a tool tip
  newState = setInShallowClone(
    newState,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'sorted'],
    SectionPanelSorting.Manual
  );

  if (sparseOverridesEnabled) {
    // Sparse overrides are enabled but this panel wasn't eligible
    const fallbackReason =
      panelKey == null ? 'no_key' : !isEligible ? 'ineligible' : 'unknown';
    recordRumAction('sparse_overrides.move', {
      usedSparseOverride: false,
      isCrossSectionMove: !isMovingToSameSection,
      fallbackReason,
    });
  }

  return [newState, undoAction];
};

const createUndoMoveActionData = (
  ref: Types.Ref,
  newState: ViewReducerState,
  active: ActiveData & {
    prevIsPanelAuto: boolean | undefined;
    prevSectionPanelsAuto: boolean | undefined;
  },
  target: TargetData & {
    prevSectionPanelsAuto: boolean | undefined;
  }
) => {
  const {undoTargetPanelRef, undoInsertDir} = getUndoData(newState, active);

  // For undo, we need to move the panel from its new location back to its original location
  // If there's a panel at the original position, use it as the target
  // Otherwise, if the section is empty, we'll need to handle that case
  return Actions.undoMovePanel(
    ref,
    {
      panelRef: active.panelRef, // The panel we just moved
      sectionRef: target.sectionRef, // It's now in the target section
      prevIsPanelAuto: active.prevIsPanelAuto,
      prevSectionPanelsAuto: target.prevSectionPanelsAuto,
    },
    {
      // If section is empty, we'll move to the first position (using the panel itself as target)
      insertDir: undoInsertDir,
      panelRef: undoTargetPanelRef, // The panel now at the original position
      sectionRef: active.sectionRef, // In the original section
      prevSectionPanelsAuto: active.prevSectionPanelsAuto,
    }
  );
};

/**
 * Creates undo action data for moves that used sparse overrides.
 * Includes the panel key and previous override for restoration.
 */
const createUndoMoveActionDataWithOverride = (
  ref: Types.Ref,
  newState: ViewReducerState,
  active: ActiveData & {
    prevIsPanelAuto: boolean | undefined;
    prevSectionPanelsAuto: boolean | undefined;
  },
  target: TargetData & {
    prevSectionPanelsAuto: boolean | undefined;
  },
  panelKey: string,
  prevOverride: PanelPlacementOverride | undefined
) => {
  const {undoTargetPanelRef, undoInsertDir} = getUndoData(newState, active);

  return Actions.undoMovePanel(
    ref,
    {
      panelRef: active.panelRef,
      sectionRef: target.sectionRef,
      prevIsPanelAuto: active.prevIsPanelAuto,
      prevSectionPanelsAuto: target.prevSectionPanelsAuto,
      // Include override info for undo
      panelKey,
      prevOverride,
    },
    {
      insertDir: undoInsertDir,
      panelRef: undoTargetPanelRef,
      sectionRef: active.sectionRef,
      prevSectionPanelsAuto: active.prevSectionPanelsAuto,
    }
  );
};
